'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdDescription, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getEvolution,
    createEvolution,
    updateEvolution,
    CreateEvolutionData,
    SHIFT_LABELS,
} from '@/lib/api/hospital'
import { getAdmissions, Admission } from '@/lib/api/hospital'

export default function NursingEvolutionFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [formData, setFormData] = useState<CreateEvolutionData>({
        admissionId: 0,
        evolutionDate: new Date().toISOString().split('T')[0],
        shift: 'MORNING',
        subjectiveData: '',
        objectiveData: '',
        assessment: '',
        interventions: '',
        evaluation: '',
        generalCondition: '',
        neurologicalStatus: '',
        respiratoryStatus: '',
        cardiovascularStatus: '',
        gastrointestinalStatus: '',
        genitourinaryStatus: '',
        skinStatus: '',
        mobilityStatus: '',
        painAssessment: '',
        pendingIssues: '',
    })

    useEffect(() => {
        loadAdmissions()
        if (isEditing && id) {
            loadEvolution(parseInt(id))
        }
    }, [id, isEditing])

    const loadAdmissions = async () => {
        try {
            const response = await getAdmissions(undefined, { status: 'ADM_ACTIVE' })
            if (response.success) {
                setAdmissions(response.result)
            }
        } catch (error) {
            console.error('Error loading admissions:', error)
        }
    }

    const loadEvolution = async (evolutionId: number) => {
        setLoading(true)
        try {
            const response = await getEvolution(evolutionId)
            if (response.success) {
                const evol = response.result
                setFormData({
                    admissionId: evol.admissionId,
                    evolutionDate: evol.evolutionDate.split('T')[0],
                    shift: evol.shift || 'MORNING',
                    subjectiveData: evol.subjectiveData || '',
                    objectiveData: evol.objectiveData || '',
                    assessment: evol.assessment || '',
                    interventions: evol.interventions || '',
                    evaluation: evol.evaluation || '',
                    generalCondition: evol.generalCondition || '',
                    neurologicalStatus: evol.neurologicalStatus || '',
                    respiratoryStatus: evol.respiratoryStatus || '',
                    cardiovascularStatus: evol.cardiovascularStatus || '',
                    gastrointestinalStatus: evol.gastrointestinalStatus || '',
                    genitourinaryStatus: evol.genitourinaryStatus || '',
                    skinStatus: evol.skinStatus || '',
                    mobilityStatus: evol.mobilityStatus || '',
                    painAssessment: evol.painAssessment || '',
                    pendingIssues: evol.pendingIssues || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar evolução')
                navigate('/hospital/evolucoes')
            }
        } catch (error) {
            toast.error('Erro ao carregar evolução')
            navigate('/hospital/evolucoes')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.admissionId) {
            toast.error('Selecione uma internação')
            return
        }

        setSaving(true)
        try {
            let response
            if (isEditing && id) {
                response = await updateEvolution(parseInt(id), formData)
            } else {
                response = await createEvolution(formData)
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Evolução atualizada com sucesso'
                        : 'Evolução criada com sucesso'
                )
                navigate('/hospital/evolucoes')
            } else {
                toast.error(response.error || 'Erro ao salvar evolucao')
            }
        } catch (error) {
            toast.error('Erro ao salvar evolucao')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/evolucoes')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Evolução' : 'Nova Evolução de Enfermagem'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize a evolução'
                                : 'Registre a evolução do paciente'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Identificação</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Internação *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.admissionId}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        admissionId: parseInt(e.target.value),
                                    })
                                }
                                required
                            >
                                <option value={0}>Selecione uma internação</option>
                                {admissions.map(adm => (
                                    <option key={adm.id} value={adm.id}>
                                        {adm.client?.name} - Leito {adm.bed?.number || 'S/N'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data *</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.evolutionDate}
                                onChange={e =>
                                    setFormData({ ...formData, evolutionDate: e.target.value })
                                }
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Turno</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.shift}
                                onChange={e =>
                                    setFormData({ ...formData, shift: e.target.value })
                                }
                            >
                                {Object.entries(SHIFT_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Avaliação SOAP</h3>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Subjetivo (S)</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Queixas do paciente..."
                                value={formData.subjectiveData}
                                onChange={e =>
                                    setFormData({ ...formData, subjectiveData: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Objetivo (O)</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Exame físico, sinais vitais..."
                                value={formData.objectiveData}
                                onChange={e =>
                                    setFormData({ ...formData, objectiveData: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Avaliação (A)</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Análise da situação..."
                                value={formData.assessment}
                                onChange={e =>
                                    setFormData({ ...formData, assessment: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Plano (P)</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-20 w-full"
                                placeholder="Intervenções realizadas..."
                                value={formData.interventions}
                                onChange={e =>
                                    setFormData({ ...formData, interventions: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Avaliação por Sistemas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Estado Geral</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-16 w-full"
                                placeholder="Descrição do estado geral..."
                                value={formData.generalCondition}
                                onChange={e =>
                                    setFormData({ ...formData, generalCondition: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Neurológico</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-16 w-full"
                                placeholder="Avaliação neurológica..."
                                value={formData.neurologicalStatus}
                                onChange={e =>
                                    setFormData({ ...formData, neurologicalStatus: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Respiratório</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-16 w-full"
                                placeholder="Avaliação respiratória..."
                                value={formData.respiratoryStatus}
                                onChange={e =>
                                    setFormData({ ...formData, respiratoryStatus: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Cardiovascular</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-16 w-full"
                                placeholder="Avaliação cardiovascular..."
                                value={formData.cardiovascularStatus}
                                onChange={e =>
                                    setFormData({ ...formData, cardiovascularStatus: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Pele/Tegumento</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-16 w-full"
                                placeholder="Avaliação da pele..."
                                value={formData.skinStatus}
                                onChange={e =>
                                    setFormData({ ...formData, skinStatus: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Mobilidade</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-16 w-full"
                                placeholder="Avaliação da mobilidade..."
                                value={formData.mobilityStatus}
                                onChange={e =>
                                    setFormData({ ...formData, mobilityStatus: e.target.value })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Pendências</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered h-24 w-full"
                            placeholder="Pendências para o próximo turno..."
                            value={formData.pendingIssues}
                            onChange={e =>
                                setFormData({ ...formData, pendingIssues: e.target.value })
                            }
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/evolucoes')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
